import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { apiRequest, getApiOrigin } from '../../../api/client';
import { useAuth } from '../../../auth/AuthContext';
import SecuritySettingsTab from '../../admin/settings/SecuritySettingsTab';
import {
    type CredentialSettings,
    type ProfileSettings,
    type SaveButtonState,
    type SessionInfo
} from '../../admin/settings/settingsTypes';
import '../../admin/quotes/quote-generator.css';
import '../../admin/settings/settings.css';

type TechSettingsSection = 'account' | 'shortcuts';

function validateNewPassword(password: string): string | null {
    if (password.length < 8) {
        return 'New password must be at least 8 characters.';
    }
    if (!/[A-Z]/.test(password)) {
        return 'New password must include at least one uppercase letter.';
    }
    if (!/[\d\W]/.test(password)) {
        return 'New password must include at least one number or special character.';
    }
    return null;
}

function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Failed to read file.'));
        reader.readAsDataURL(file);
    });
}

export default function TechSettingsPage(): React.JSX.Element {
    const { refreshSession } = useAuth();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState<TechSettingsSection>('account');
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<string | null>(null);
    const [saveState, setSaveState] = useState<SaveButtonState>('idle');
    const [saving, setSaving] = useState(false);
    const [securityLoading, setSecurityLoading] = useState(true);
    const [avatarError, setAvatarError] = useState<string | null>(null);

    const [profile, setProfile] = useState<ProfileSettings>({
        displayName: '',
        avatarBase64: null,
        role: 'technician'
    });
    const [credentials, setCredentials] = useState<CredentialSettings | null>(null);
    const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordErrors, setPasswordErrors] = useState<{
        newPassword?: string;
        confirmPassword?: string;
        submit?: string;
    }>({});
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [googleDisconnecting, setGoogleDisconnecting] = useState(false);
    const [googleDisconnectConfirm, setGoogleDisconnectConfirm] = useState(false);

    const saveResetTimer = useRef<number | null>(null);

    const clearSaveResetTimer = () => {
        if (saveResetTimer.current) {
            window.clearTimeout(saveResetTimer.current);
            saveResetTimer.current = null;
        }
    };

    const markSaved = () => {
        clearSaveResetTimer();
        setSaveState('saved');
        saveResetTimer.current = window.setTimeout(() => setSaveState('idle'), 2000);
    };

    const showToast = (message: string) => {
        setToast(message);
        window.setTimeout(() => setToast(null), 3200);
    };

    const loadSecurityData = useCallback(async () => {
        setSecurityLoading(true);
        setAvatarError(null);
        setGoogleDisconnectConfirm(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
        setPasswordSuccess(false);
        try {
            const [profileRes, sessionRes] = await Promise.all([
                apiRequest<{
                    profile: ProfileSettings;
                    credentials: CredentialSettings;
                }>('/api/settings/profile'),
                apiRequest<{ session: SessionInfo }>('/api/settings/session')
            ]);
            setProfile(profileRes.profile);
            setCredentials(profileRes.credentials);
            setSessionInfo(sessionRes.session);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load account settings.');
        } finally {
            setSecurityLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadSecurityData();
    }, [loadSecurityData]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('linked') === 'true') {
            showToast('Google account connected successfully.');
        }
        if (params.get('link_error') === 'already_linked') {
            setError('This Google account is already linked to another user.');
        } else if (params.get('link_error')) {
            setError('Google account could not be connected. Please try again.');
        }
        if (params.get('linked') || params.get('link_error')) {
            navigate('/technician/settings', { replace: true });
        }
    }, [navigate]);

    useEffect(() => () => clearSaveResetTimer(), []);

    const saveProfile = async () => {
        const displayName = profile.displayName.trim();
        if (!displayName) {
            setAvatarError(null);
            setError('Display name is required.');
            setSaveState('idle');
            return;
        }

        setSaving(true);
        setSaveState('saving');
        setError(null);
        setAvatarError(null);
        try {
            const res = await apiRequest<{
                profile: ProfileSettings;
                credentials: CredentialSettings;
            }>('/api/settings/profile', {
                method: 'PATCH',
                json: {
                    displayName,
                    avatarBase64: profile.avatarBase64
                }
            });
            setProfile(res.profile);
            setCredentials(res.credentials);
            await refreshSession();
            markSaved();
        } catch (e: unknown) {
            setSaveState('idle');
            setError(e instanceof Error ? e.message : 'Failed to save profile.');
        } finally {
            setSaving(false);
        }
    };

    const handleAvatarUpload = async (file: File) => {
        setAvatarError(null);
        if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) {
            setAvatarError('Avatar must be a PNG or JPG image.');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setAvatarError('Avatar must be 2 MB or smaller.');
            return;
        }
        try {
            const dataUrl = await fileToDataUrl(file);
            setProfile((prev) => ({ ...prev, avatarBase64: dataUrl }));
        } catch (e: unknown) {
            setAvatarError(e instanceof Error ? e.message : 'Failed to read image.');
        }
    };

    const handlePasswordFieldChange = (
        field: 'currentPassword' | 'newPassword' | 'confirmPassword',
        value: string
    ) => {
        setPasswordForm((prev) => ({ ...prev, [field]: value }));
        setPasswordSuccess(false);
        setPasswordErrors((prev) => {
            const next = { ...prev, submit: undefined };
            if (field === 'newPassword') next.newPassword = undefined;
            if (field === 'confirmPassword') next.confirmPassword = undefined;
            return next;
        });
    };

    const handleUpdatePassword = async () => {
        const nextErrors: typeof passwordErrors = {};
        const newPasswordError = validateNewPassword(passwordForm.newPassword);
        if (newPasswordError) nextErrors.newPassword = newPasswordError;
        if (passwordForm.confirmPassword !== passwordForm.newPassword) {
            nextErrors.confirmPassword = 'Confirm password must match new password.';
        }
        if (Object.keys(nextErrors).length > 0) {
            setPasswordErrors(nextErrors);
            return;
        }

        setPasswordSaving(true);
        setPasswordErrors({});
        setPasswordSuccess(false);
        try {
            await apiRequest('/api/auth/change-password', {
                method: 'POST',
                json: passwordForm
            });
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setPasswordSuccess(true);
            await refreshSession();
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Failed to update password.';
            if (message.toLowerCase().includes('current password')) {
                setPasswordErrors({ submit: 'Current password is incorrect' });
            } else {
                setPasswordErrors({ submit: message });
            }
        } finally {
            setPasswordSaving(false);
        }
    };

    const handleConnectGoogle = () => {
        window.location.assign(`${getApiOrigin()}/api/auth/google/link`);
    };

    const handleConfirmDisconnectGoogle = async () => {
        setGoogleDisconnecting(true);
        setError(null);
        try {
            await apiRequest('/api/auth/google/link', { method: 'DELETE' });
            setGoogleDisconnectConfirm(false);
            showToast('Google account disconnected.');
            await loadSecurityData();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to disconnect Google account.');
        } finally {
            setGoogleDisconnecting(false);
        }
    };

    const shortcuts = [
        {
            title: 'Job list',
            meta: 'Open assigned work orders and building checklists.',
            to: '/technician/JobList'
        },
        {
            title: 'Job log',
            meta: 'Submit daily work-order forms for jobs with attached templates.',
            to: '/technician/JobLog'
        },
        {
            title: 'Time sheet',
            meta: 'Review and edit logged hours.',
            to: '/technician/TimeSheet'
        },
        {
            title: 'Export report',
            meta: 'Download CSV summaries and inspection PDFs.',
            to: '/technician/ExportReport'
        }
    ];

    return (
        <AppShell>
            <div className="quote-page settings-page">
                <div className="quote-container settings-container">
                    <header className="quote-header" style={{ marginBottom: 24 }}>
                        <div>
                            <h1 style={{ fontSize: '2.1rem', marginBottom: 8 }}>Settings</h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0 }}>
                                Your account, sign-in options, and quick links to field tools.
                            </p>
                        </div>
                    </header>

                    {error ? <div className="banner banner-error">{error}</div> : null}
                    {toast ? <div className="banner banner-success">{toast}</div> : null}

                    <div className="settings-layout">
                        <aside className="quote-card settings-sidebar">
                            <button
                                type="button"
                                className={`settings-sidebar-item ${activeSection === 'account' ? 'is-active' : ''}`}
                                onClick={() => setActiveSection('account')}
                            >
                                Account
                            </button>
                            <button
                                type="button"
                                className={`settings-sidebar-item ${activeSection === 'shortcuts' ? 'is-active' : ''}`}
                                onClick={() => setActiveSection('shortcuts')}
                            >
                                Field tools
                            </button>
                        </aside>

                        <div className="settings-content">
                            {activeSection === 'account' ? (
                                <>
                                    <SecuritySettingsTab
                                        profile={profile}
                                        credentials={credentials}
                                        session={sessionInfo}
                                        profileLoading={securityLoading}
                                        passwordForm={passwordForm}
                                        passwordErrors={passwordErrors}
                                        passwordSaving={passwordSaving}
                                        passwordSuccess={passwordSuccess}
                                        googleDisconnecting={googleDisconnecting}
                                        googleDisconnectConfirm={googleDisconnectConfirm}
                                        onProfileChange={(patch) => setProfile((prev) => ({ ...prev, ...patch }))}
                                        onAvatarUpload={handleAvatarUpload}
                                        onRemoveAvatar={() => setProfile((prev) => ({ ...prev, avatarBase64: null }))}
                                        avatarError={avatarError}
                                        onPasswordFieldChange={handlePasswordFieldChange}
                                        onUpdatePassword={handleUpdatePassword}
                                        onSignOutOthers={() => showToast('No other active sessions found.')}
                                        onConnectGoogle={handleConnectGoogle}
                                        onRequestDisconnectGoogle={() => setGoogleDisconnectConfirm(true)}
                                        onCancelDisconnectGoogle={() => setGoogleDisconnectConfirm(false)}
                                        onConfirmDisconnectGoogle={handleConfirmDisconnectGoogle}
                                    />
                                    <div className="quote-actions settings-save-row">
                                        <button
                                            type="button"
                                            className={`btn-primary settings-save-btn${saveState === 'saved' ? ' is-saved' : ''}`}
                                            onClick={() => void saveProfile()}
                                            disabled={saving || securityLoading || saveState === 'saving'}
                                        >
                                            {saveState === 'saving'
                                                ? 'Saving…'
                                                : saveState === 'saved'
                                                  ? 'Saved ✓'
                                                  : 'Save profile'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <section className="quote-card">
                                    <div className="quote-section-title">Field tools</div>
                                    <p className="settings-hint">
                                        Jump straight to the workflows you use most in the field.
                                    </p>
                                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                                        {shortcuts.map((item) => (
                                            <li key={item.to}>
                                                <Link
                                                    to={item.to}
                                                    className="settings-sidebar-item"
                                                    style={{ display: 'block', textDecoration: 'none' }}
                                                >
                                                    <span style={{ display: 'block', fontWeight: 700 }}>{item.title}</span>
                                                    <span style={{ display: 'block', marginTop: 4, fontSize: 13, opacity: 0.75 }}>
                                                        {item.meta}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
